import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

// Select en el que se escribe para filtrar en vez de buscar en una lista larga.
// opciones: [{ value, label, detalle?, busqueda? }] — `busqueda` es texto extra
// por el que también se puede encontrar (SKU, talla, DNI...).
// Sin iconos superpuestos: globals.css pone todo input por encima de los
// .absolute, así que no se verían. La lista se dibuja en un portal con position:fixed para que no la recorte
// el overflow-hidden del Modal.

const normalizar = (t) =>
    String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const BuscadorSelect = ({ opciones, value, onChange, placeholder = 'Escribe para buscar...', className = '' }) => {
    const [abierto, setAbierto] = useState(false);
    const [texto, setTexto] = useState('');
    const [activo, setActivo] = useState(0);
    const [pos, setPos] = useState(null);
    const inputRef = useRef(null);
    const listaRef = useRef(null);

    const seleccionada = opciones.find((o) => String(o.value) === String(value));

    const filtradas = useMemo(() => {
        const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
        if (!palabras.length) return opciones;
        return opciones.filter((o) => {
            const heno = normalizar(`${o.label} ${o.detalle || ''} ${o.busqueda || ''}`);
            return palabras.every((p) => heno.includes(p));
        });
    }, [opciones, texto]);

    const recalcularPos = () => {
        const r = inputRef.current?.getBoundingClientRect();
        if (!r) return;
        const abajo = window.innerHeight - r.bottom;
        const alto = Math.min(288, Math.max(abajo, r.top) - 12);
        setPos(abajo >= 200 || abajo >= r.top
            ? { left: r.left, width: r.width, top: r.bottom + 4, maxHeight: alto }
            : { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4, maxHeight: alto });
    };

    useLayoutEffect(() => {
        if (!abierto) return undefined;
        recalcularPos();
        window.addEventListener('resize', recalcularPos);
        window.addEventListener('scroll', recalcularPos, true);
        return () => {
            window.removeEventListener('resize', recalcularPos);
            window.removeEventListener('scroll', recalcularPos, true);
        };
    }, [abierto]);

    useEffect(() => { setActivo(0); }, [texto]);

    useEffect(() => {
        listaRef.current?.querySelector(`[data-idx="${activo}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [activo]);

    const abrir = () => { setTexto(''); setAbierto(true); };
    const cerrar = () => { setAbierto(false); setTexto(''); };

    const elegir = (opcion) => {
        onChange(String(opcion.value));
        cerrar();
        inputRef.current?.blur();
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!abierto) abrir();
            else setActivo((i) => Math.min(i + 1, filtradas.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActivo((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            if (abierto && filtradas[activo]) {
                e.preventDefault();
                elegir(filtradas[activo]);
            }
        } else if (e.key === 'Escape' && abierto) {
            // Que el Escape cierre solo la lista, no el modal entero.
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            cerrar();
        }
    };

    return (
        <div className={`relative ${className}`}>
            <input
                ref={inputRef}
                type="text"
                value={abierto ? texto : (seleccionada?.label || '')}
                onChange={(e) => { setTexto(e.target.value); if (!abierto) setAbierto(true); }}
                onFocus={abrir}
                onClick={() => { if (!abierto) abrir(); }}
                onBlur={cerrar}
                onKeyDown={onKeyDown}
                placeholder={abierto && seleccionada ? seleccionada.label : placeholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                autoComplete="off"
            />
            {abierto && pos && createPortal(
                <ul
                    ref={listaRef}
                    style={{ position: 'fixed', left: pos.left, width: Math.max(pos.width, 260), top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
                    className="z-[100] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm"
                    // Evita que el input pierda el foco (y cierre la lista) al hacer clic.
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {filtradas.length === 0 && (
                        <li className="px-3 py-2 text-gray-400">Sin resultados para "{texto}"</li>
                    )}
                    {filtradas.map((o, idx) => (
                        <li
                            key={o.value}
                            data-idx={idx}
                            onClick={() => elegir(o)}
                            onMouseEnter={() => setActivo(idx)}
                            className={`px-3 py-2 cursor-pointer ${idx === activo ? 'bg-blue-50' : ''} ${String(o.value) === String(value) ? 'font-semibold' : ''}`}
                        >
                            <div className="text-gray-900">{o.label}</div>
                            {o.detalle && <div className="text-xs text-gray-500">{o.detalle}</div>}
                        </li>
                    ))}
                </ul>,
                document.body,
            )}
        </div>
    );
};

export default BuscadorSelect;
